import type { FC, ReactNode } from 'react';
import { Accordion, Link } from '@highereducation/hen-ui';

export type RankingsMethodologyVariant = 'enhanced' | 'regular' | 'alternate';

export interface RankingsMethodologyProps {
  /**
   * Methodology copy variant. Matches the legacy `methodology` prop on
   * `SonicRanking` / `HowWeRank`:
   *
   *   - `enhanced`  → full intro + 4-card weighted criteria grid. The default
   *                   psychology.org best-list experience.
   *   - `alternate` → shorter copy (intro + "reserves the right to exclude"
   *                   paragraph) for ranked lists that don't follow the
   *                   standard 4-axis breakdown (community colleges,
   *                   doctoral programs, affordability-prioritized rankings).
   *                   Ported from legacy `AlternateMethodology` psychology
   *                   branch under EDUN-11791.
   *   - `regular`   → BC default. Not yet ported -- BC ships its own copy +
   *                   migration when its HEN cutover lands. Currently falls
   *                   through to `enhanced` as a placeholder.
   */
  variant?: RankingsMethodologyVariant;
  /**
   * Optional year string (e.g. "2026") rendered as "This ranking is from
   * 2026." in the body copy. When omitted the sentence is left out entirely —
   * this is intentional: many psychology.org lists are not refreshed every
   * year, and old lists with no year should not be stamped with an outdated
   * or auto-derived date. Editors only supply this attribute when they have
   * actually pulled a new school list for that year.
   */
  year?: string;
  /**
   * Site brand name (e.g. "Psychology.org"). Defaults to "This site"
   * (sentence-case so it reads naturally at the start of a sentence) when
   * the shortcode carries no `sitename` attribute. Surfacing this from
   * publisher config is tracked as a follow-up to the EDUN-11791 migration.
   */
  siteName?: string;
}

interface CriterionCard {
  weight: string;
  title: string;
  description: ReactNode;
}

const ENHANCED_CRITERIA: CriterionCard[] = [
  {
    weight: '30%',
    title: 'Academic Quality',
    description:
      "Factors including retention rate, graduation rate, and student-to-faculty ratio help provide insight into a school's academic quality.",
  },
  {
    weight: '20%',
    title: 'Affordability',
    description:
      'Data on net cost, post-graduation debt, and the percentage of students awarded loans and other aid provide information on affordability.',
  },
  {
    weight: '20%',
    title: 'Accessibility',
    description:
      "Online degree availability and online enrollment rates can reflect a school's commitment to supporting remote learners.",
  },
  {
    weight: '30%',
    title: 'School Reputation',
    description:
      'Competitive admissions, high enrollment, and strong alumni earnings can indicate a positive reputation among students and employers.',
  },
];

/**
 * Shared intro paragraph that opens both `enhanced` and `alternate` variants.
 * Kept inline (not extracted to a separate component) so the variant bodies
 * can compose around it differently -- enhanced continues into the 4-card
 * grid, alternate continues into the "reserves the right to exclude"
 * disclosure. Mirrors the legacy psychology-branch copy from
 * `EnhancedMethodology.tsx` and `AlternateMethodology.tsx`.
 */
function IntroParagraph({
  siteName,
  variant,
}: {
  siteName: string;
  variant: 'enhanced' | 'alternate';
}) {
  // The enhanced variant continues "...check out our full ranking methodology
  // OR review the summary below." Alternate stops at the methodology link
  // because there's no summary to review. This matches the legacy split.
  if (variant === 'alternate') {
    return (
      <p>
        {siteName} ranks education programs using data from reputable sources
        like the U.S. Department of Education. Our vetting process also includes
        a quality assurance team that assesses datasets for accuracy.
      </p>
    );
  }

  return (
    <p>
      {siteName} ranks education programs using data from reputable sources like
      the U.S. Department of Education. Our vetting process also includes a
      quality assurance team that assesses datasets for accuracy. For more
      information about how {siteName} evaluates and ranks education programs,
      check out{' '}
      <Link href='/methodology/' variant='default' underline='always'>
        our full ranking methodology
      </Link>{' '}
      or review the summary below.
    </p>
  );
}

function AlternateMethodologyBody({
  year,
  siteName,
}: {
  year?: string;
  siteName: string;
}) {
  return (
    <div
      className='flex flex-col gap-md text-b4 text-body'
      data-testid='rankings-methodology-body'
      data-variant='alternate'
    >
      <IntroParagraph siteName={siteName} variant='alternate' />
      <p>
        {siteName} reserves the right to exclude any schools that lack
        significant data from the rankings to ensure accurate calculations.
        {year ? ` This ranking is from ${year}.` : ''} For more about how{' '}
        {siteName} evaluates and ranks education programs, visit{' '}
        <Link href='/methodology/' variant='default' underline='always'>
          our full ranking methodology
        </Link>
        .
      </p>
    </div>
  );
}

function EnhancedMethodologyBody({
  year,
  siteName,
}: {
  year?: string;
  siteName: string;
}) {
  return (
    <div
      className='flex flex-col gap-md text-b4 text-body'
      data-testid='rankings-methodology-body'
      data-variant='enhanced'
    >
      <IntroParagraph siteName={siteName} variant='enhanced' />
      <p>
        {year ? `This ranking is from ${year}. ` : ''}
        To qualify for our rankings lists, each school must be an accredited
        public or private nonprofit institution. Each eligible school is then
        ranked based on a formula that measures:
      </p>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-md'>
        {ENHANCED_CRITERIA.map((criterion) => (
          <div
            key={criterion.title}
            className='bg-white rounded-sm p-md flex flex-col gap-xs'
          >
            <div className='flex flex-row items-center gap-sm'>
              <span className='bg-secondary-50 text-heading rounded-sm px-sm py-xxs text-h5 font-bold'>
                {criterion.weight}
              </span>
              <h4 className='text-h5 font-bold text-heading'>
                {criterion.title}
              </h4>
            </div>
            <p className='text-b5 text-subtext leading-b5'>
              {criterion.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * RankingsMethodology
 *
 * Restores the "How We Rank Schools" methodology disclosure under
 * `RankingsAdapter` (EDUN-11791). Mirrors the legacy `SonicRanking` →
 * `HowWeRank` auto-injection, but rendered through the token-driven hen-ui
 * `Accordion` composite for WCAG 2.1 AA parity.
 *
 * Tenant scoping is handled at the WordPress content layer: psychology.org's
 * `[hen-ui-rankings ...]` shortcodes opt in by passing `methodology="enhanced"`
 * (see `se-utilities/hen-db-migrations/psychology.org`). Other tenants are
 * unaffected until they opt in via their own content migration.
 */
export const RankingsMethodology: FC<RankingsMethodologyProps> = ({
  variant = 'enhanced',
  year,
  siteName = 'This site',
}) => {
  // `regular` falls through to `enhanced` until BC ports its own copy under
  // the BC HEN cutover -- documented in `RankingsMethodologyProps.variant`.
  const body =
    variant === 'alternate' ? (
      <AlternateMethodologyBody year={year} siteName={siteName} />
    ) : (
      <EnhancedMethodologyBody year={year} siteName={siteName} />
    );

  return (
    <div className='mt-lg' data-testid='rankings-methodology'>
      <Accordion
        type='single'
        collapsible
        variant='primary'
        items={[
          {
            title: 'How We Rank Schools',
            value: `methodology-${variant}`,
            content: body,
          },
        ]}
      />
    </div>
  );
};
